import { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion } from "framer-motion";
import { Sparkles, Send, Bot, User, Loader2, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Apa fasilitas PPN untuk penyerahan JKP dari KEK ke TLDDP?",
  "BKP Berwujud dari TLDDP ke FTZ, dipungut PPN atau tidak?",
  "Apa itu BKP tertentu yang tidak dipungut PPN ke KEK?",
  "Bagaimana perlakuan PPN ekspor dari TPB ke luar negeri?",
];

export default function TanyaAI() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(`web-${Date.now()}`);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q, session_id: sessionRef.current }),
      });
      if (!res.ok) throw new Error("Gagal menghubungi AI");
      const data = await res.json();
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Maaf, terjadi kendala saat menghubungi AI. Silakan coba lagi sebentar." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="tanya-ai-section">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-gradient-to-r from-[#0a3d62] to-[#1e5f8c] px-6 py-5 text-white">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold">Tanya AI</h2>
          <p className="text-xs text-sky-100/90">Asisten fasilitas PPN — bersumber dari tabel skema & peraturan resmi</p>
        </div>
      </div>

      {/* Chat window */}
      <div ref={scrollRef} className="max-h-[52vh] min-h-[280px] space-y-4 overflow-y-auto px-4 py-5 sm:px-6" data-testid="chat-window">
        {messages.length === 0 && (
          <div className="py-6 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#0a3d62]/8 text-[#0a3d62]">
              <Bot className="h-6 w-6" />
            </div>
            <p className="mt-3 text-sm font-medium text-slate-500">Tanyakan apa pun seputar fasilitas PPN antar kawasan.</p>
            <div className="mx-auto mt-5 grid max-w-xl grid-cols-1 gap-2 sm:grid-cols-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  data-testid={`suggestion-${i}`}
                  onClick={() => send(s)}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left text-sm text-slate-700 transition-colors hover:border-[#0a3d62]/40 hover:bg-[#0a3d62]/5"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.role}`}>
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${m.role === "user" ? "bg-[#0a3d62] text-white" : "bg-[#0a3d62]/8 text-[#0a3d62]"}`}>
              {m.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
            </div>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-[#0a3d62] text-white" : "border border-slate-200 bg-slate-50 text-slate-800"}`}>
              {m.role === "user" ? (
                <span className="whitespace-pre-line">{m.content}</span>
              ) : (
                <div className="ppn-markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                </div>
              )}
            </div>
          </motion.div>
        ))}

        {loading && (
          <div className="flex gap-3" data-testid="chat-loading">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#0a3d62]/8 text-[#0a3d62]">
              <Bot className="h-4 w-4" />
            </div>
            <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Sedang menyusun jawaban…
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-slate-100 p-4 sm:px-6">
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="flex items-end gap-2"
        >
          <textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
            placeholder="Tulis pertanyaan Anda…"
            className="max-h-32 min-h-[46px] flex-1 resize-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition-colors focus:border-[#0a3d62] focus:ring-2 focus:ring-[#0a3d62]/20"
          />
          <button
            type="submit"
            data-testid="chat-send-btn"
            disabled={loading || !input.trim()}
            className="inline-flex h-[46px] items-center gap-2 rounded-xl bg-[#0a3d62] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#0a3d62]/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            <span className="hidden sm:inline">Kirim</span>
          </button>
        </form>

        {/* AI Disclaimer */}
        <div className="mt-3 flex gap-2 rounded-lg bg-slate-50 px-3 py-2.5 text-xs text-slate-500" data-testid="ai-disclaimer">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span>Jawaban AI ini bersifat informasi umum. Untuk kepastian hukum dan kepatuhan, silakan cek kembali ke peraturan resmi intansi terkait.</span>
        </div>
      </div>
    </section>
  );
}
